/// <reference types="../../../../../env.d.ts" />

import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TranslationService } from '@kanek/shared-i18n';
import { ClientContextService, FirebaseService } from '@kanek/shared-data-access';
import { doc, getDoc } from 'firebase/firestore';

/**
 * Service responsible for initializing the correct language/locale based on client database settings.
 * Uses database-driven locale detection instead of hardcoded client ID patterns.
 */

@Injectable({
  providedIn: 'root'
})
export class LanguageInitializationService {
  private translationService = inject(TranslationService);
  private clientContextService = inject(ClientContextService);
  private firebaseService = inject(FirebaseService);
  private titleService = inject(Title);

  /**
   * Initialize the appropriate language based on client database locale settings.
   * This method:
   * 1. Gets client ID from environment variables
   * 2. Fetches client locale from database
   * 3. Maps locale to supported app locale
   * 4. Sets the translation service locale
   * 5. Updates page title with client name
   */
  async initializeLanguage(): Promise<void> {
    try {
      // Get client ID from environment (build-time configuration)
      const clientId = this.getClientIdFromEnvironment();
      
      if (!clientId) {
        console.warn('❌ CLIENT_ID not found in environment variables, defaulting to English');
        await this.translationService.setLocale('en-us');
        return;
      }

      // Set the client context for branding consistency
      this.clientContextService.setSelectedClient(clientId);

      // Get the client's locale from their database settings
      const clientLocale = await this.getClientLocale(clientId);
      
      // Map client locale to supported application locale
      const appLocale = this.mapClientLocaleToAppLocale(clientLocale);
      
      console.log(`🌐 Setting locale to ${appLocale} based on client locale ${clientLocale} for client ${clientId}`);
      await this.translationService.setLocale(appLocale);
      
    } catch (error) {
      console.error('❌ Error initializing language:', error);
      // Fallback to English on error
      await this.translationService.setLocale('en-us');
    }
  }

  /**
   * Hardcoded locale mapping as fallback when database access is not available
   * This ensures French-Canadian works even without database permissions
   */
  private getHardcodedLocaleForClient(clientId: string): string {
    // Known French-Canadian clients
    const frenchCanadianClients = [
      'lotoquebec',
      'loto-quebec', 
      'quebec',
      'saq'  // Société des alcools du Québec (if added later)
    ];
    
    if (frenchCanadianClients.includes(clientId.toLowerCase())) {
      console.log(`🇨🇦 Client ${clientId} identified as French-Canadian via hardcoded mapping`);
      return 'fr-CA';
    }
    
    console.log(`🌍 Client ${clientId} using default English locale`);
    return 'en-US';
  }

  /**
   * Get the client's locale setting from their configuration
   */
  private async getClientLocale(clientId: string): Promise<string> {
    try {
      // First, try to get client settings from the client context service (fast path)
      const currentClient = this.clientContextService.getCurrentClient();
      
      if (currentClient?.settings?.locale) {
        console.log(`📍 Found client locale in context: ${currentClient.settings.locale} for client ${currentClient.name}`);
        return currentClient.settings.locale;
      }

      // Experience app doesn't load client data into context, so fetch directly from database
      console.log(`📍 Client not in context, fetching locale from database for client: ${clientId}`);
      
      try {
        // Get the database instance (use central for client configuration)
        const db = this.firebaseService.getCentralDatabase();
        const clientDocRef = doc(db, 'clients', clientId);
        const clientDoc = await getDoc(clientDocRef);
        
        if (clientDoc.exists()) {
          const clientData = clientDoc.data();
          const locale = clientData?.['settings']?.['locale'] || clientData?.['locale'];
          
          // Extract client name for page title
          const clientName = clientData?.['name'] || clientData?.['displayName'] || clientData?.['title'];
          if (clientName) {
            this.updatePageTitle(clientName);
          }
          
          if (locale) {
            console.log(`📍 Found client locale in database: ${locale} for client ${clientId}`);
            return locale;
          } else {
            console.log(`📍 No locale field found in database for client ${clientId}, using fallback`);
          }
        } else {
          console.log(`📍 Client document not found in database: ${clientId}`);
        }
      } catch (dbError) {
        console.warn('⚠️ Cannot access client database (likely permission issue):', dbError);
        console.log('📝 Note: Experience app runs anonymously, client config requires Firestore rules update');
        
        // Set fallback title when database is not accessible
        this.updatePageTitleFallback(clientId);
        
        // For now, we can implement a simple client-to-locale mapping as fallback
        console.log('📍 Using hardcoded locale mapping as fallback');
        return this.getHardcodedLocaleForClient(clientId);
      }

      // Fallback to default
      console.log(`📍 Using default locale en-US for client ${clientId}`);
      return 'en-US';
      
    } catch (error) {
      console.error('Error getting client locale:', error);
      return 'en-US';
    }
  }

  /**
   * Map client locale to supported application locale
   */
  private mapClientLocaleToAppLocale(clientLocale: string): string {
    // Normalize the locale string
    const normalizedLocale = clientLocale.toLowerCase().trim();
    
    // French-Canadian variants
    if (normalizedLocale === 'fr-ca' || 
        normalizedLocale === 'fr_ca' ||
        normalizedLocale === 'french-canadian' ||
        normalizedLocale === 'french-canada' ||
        normalizedLocale === 'français-canadien') {
      console.log('🇨🇦 Client locale indicates French-Canadian, using fr-ca');
      return 'fr-ca';
    }
    
    // Other French variants (could expand for France French in future)
    if (normalizedLocale.startsWith('fr')) {
      console.log('🇫🇷 Client locale indicates French, using fr-ca as closest match');
      return 'fr-ca';
    }
    
    // English variants (handle both en-US and en-us formats)
    if (normalizedLocale === 'en-us' || 
        normalizedLocale === 'en_us' ||
        normalizedLocale === 'en-ca' ||  // English-Canadian 
        normalizedLocale === 'en_ca' ||
        normalizedLocale === 'english' ||
        normalizedLocale.startsWith('en')) {
      console.log('🇺🇸 Client locale indicates English, using en-us');
      return 'en-us';
    }
    
    // Default fallback
    console.log(`❓ Unknown client locale '${clientLocale}', defaulting to en-us`);
    return 'en-us';
  }

  /**
   * Get current locale for debugging/display purposes
   */
  getCurrentLocale(): string {
    return this.translationService.getCurrentLocaleValue();
  }

  /**
   * Manually switch language (for testing or user preference)
   */
  async switchLanguage(locale: string): Promise<void> {
    await this.translationService.setLocale(locale);
    console.log(`🌐 Language switched to ${locale}`);
  }

  /**
   * Update the HTML page title with client name
   */
  private updatePageTitle(clientName: string): void {
    const newTitle = `${clientName} | Powered by Kanek`;
    this.titleService.setTitle(newTitle);
    console.log(`📄 Page title updated: ${newTitle}`);
  }

  /**
   * Update page title with fallback client names when database is not accessible
   */
  private updatePageTitleFallback(clientId: string): void {
    // Hardcoded client name mappings for when database is not accessible
    const clientNameMapping: Record<string, string> = {
      'lotoquebec': 'Loto-Québec',
      'loto-quebec': 'Loto-Québec',
      'quebec': 'Loto-Québec',
      // Add more client mappings as needed
    };

    const clientName = clientNameMapping[clientId] || 'Experience';
    const newTitle = `${clientName} | Powered by Kanek`;
    this.titleService.setTitle(newTitle);
    console.log(`📄 Page title updated (fallback): ${newTitle}`);
  }

  /**
   * Get client ID from environment variables with proper error handling
   */
  private getClientIdFromEnvironment(): string | null {
    try {
      return import.meta.env['_NGX_ENV_CLIENT_ID'] || null;
    } catch (error) {
      console.error('Error accessing environment variables:', error);
      return null;
    }
  }
}